import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Music, Plus, CheckCircle } from 'lucide-react';
import { useSocket } from '../hooks/useSocket';
import { useAuth } from '../hooks/useAuth';
import { useSongSearch } from '../hooks/useSongSearch';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { canManageQueue, getRole, sessionSettings } from '../utils/roles';
import { useDialog } from '../hooks/useDialog';
import { usePartySuggestions } from '../hooks/usePartySuggestions';
import { useGoTos } from '../hooks/useGoTos';

const SongSearch = ({ session, onClose }) => {
  const { suggestSong } = useSocket();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  // Locked to the session's service — a Spotify song suggested into an
  // Apple session (or vice versa) can never be played by the host's player
  const selectedMusicService = session.musicService || 'spotify';

  // Hosts and co-hosts add directly via the REST endpoint; guests suggest
  // over the socket (which places songs directly when approval is off)
  const isModerator = canManageQueue(session, user?.uid);
  const settings = sessionSettings(session);
  const openMode = settings.requireApproval === false;
  const dialogRef = useDialog(onClose);
  
  const { data: searchResults, isLoading, error: searchError, refetch } = useSongSearch(
    query,
    selectedMusicService,
    query.length > 2,
    session.id
  );

  const handleSearch = (e) => {
    e.preventDefault();
    console.log('[SongSearch] Searching for:', query, 'using:', selectedMusicService);
    if (query.trim().length > 2) {
      refetch();
    }
  };

  const handleSuggestSong = (song) => {
    try {
      // Ensure the song has the musicService field
      const songWithService = {
        ...song,
        musicService: selectedMusicService
      };
      console.log('[SongSearch] Suggesting song:', songWithService, 'for session:', session.id);
      suggestSong(songWithService);
      toast.success(openMode ? `Added "${song.name}" to the playlist` : `Suggested "${song.name}"`);
      onClose();
    } catch (error) {
      console.error('Suggest song error:', error);
      toast.error('Failed to suggest song');
    }
  };

  const handleAddDirectly = async (song, override = false) => {
    try {
      // Add song directly to playlist (host only)
      const songWithService = {
        ...song,
        musicService: selectedMusicService
      };

      // Call backend API to add song directly to playlist
      const response = await api.post(`/api/playlist/${session.id}/add-direct`, {
        song: songWithService,
        override
      });

      if (response.status === 200) {
        toast.success(`Added "${song.name}" to playlist!`);
        onClose();
      }
    } catch (error) {
      const data = error.response?.data;
      // Played already — the host gets a one-tap "run it back"
      if (data?.code === 'already_played' && !override) {
        if (window.confirm(`"${song.name}" already played tonight. Run it back?`)) {
          handleAddDirectly(song, true);
        }
        return;
      }
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      console.error('Add song directly error:', error);
      toast.error('Failed to add song to playlist');
    }
  };

  const formatDuration = (duration) => {
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Party-based picks + personal go-tos shown before the user types anything
  const showingPicks = query.trim().length < 3;
  const { data: partyPicks = [], isLoading: picksLoading } = usePartySuggestions(session, showingPicks);
  const { data: goTos = [] } = useGoTos(showingPicks);
  const hideExplicit = !settings.explicitAllowed && getRole(session, user?.uid) !== 'host';
  const visiblePicks = (hideExplicit ? partyPicks.filter((s) => !s.explicit) : partyPicks).slice(0, 3);
  // Go-tos: same service as this session, policy-filtered, not already picked
  const visibleGoTos = goTos
    .filter((s) => (s.musicService || 'apple') === selectedMusicService)
    .filter((s) => !hideExplicit || !s.explicit)
    .filter((s) => !visiblePicks.some((p) => p.id === s.id))
    .slice(0, 4);

  // One row renderer shared by search results and party picks
  const renderSongRow = (song) => (
    <motion.div
      key={song.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-700 rounded-lg p-4 border border-gray-600 hover:border-purple-500 transition-colors"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
          {/* Song Image */}
          {song.image && (
            <img
              src={song.image}
              alt={song.name}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover shrink-0"
            />
          )}

          {/* Song Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white truncate flex items-center gap-1.5">
              <span className="truncate min-w-0">{song.name}</span>
              {song.explicit && (
                <span
                  className="text-[10px] font-bold bg-gray-500 text-white px-1 rounded shrink-0"
                  title="Explicit"
                >
                  E
                </span>
              )}
            </h3>
            <p className="text-gray-400 text-sm truncate">
              {song.artist} • {song.album}
            </p>
            <div className="flex items-center space-x-4 mt-1 text-xs text-gray-400">
              <span>{formatDuration(song.duration)}</span>
              <span className="capitalize">{song.musicService || selectedMusicService}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2 shrink-0 sm:ml-4">
          {isModerator ? (
            <>
              <button
                onClick={() => handleAddDirectly(song)}
                className="btn-primary flex items-center text-xs sm:text-sm"
              >
                <CheckCircle className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Add to Playlist</span>
              </button>
              <button
                onClick={() => handleSuggestSong(song)}
                className="btn-secondary flex items-center text-xs sm:text-sm"
              >
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Suggest Only</span>
              </button>
            </>
          ) : (
            <button
              onClick={() => handleSuggestSong(song)}
              className="btn-primary flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              {openMode ? 'Add' : 'Suggest'}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={isModerator || openMode ? 'Add songs to playlist' : 'Suggest a song'}
      >
        <motion.div
          ref={dialogRef}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gray-800 rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700 shrink-0">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-white">
                {isModerator || openMode ? 'Add Songs to Playlist' : 'Suggest a Song'}
              </h2>
              {isModerator && (
                <p className="text-sm text-gray-400 mt-1">
                  You can add songs directly to the playlist
                </p>
              )}
              {!settings.explicitAllowed && (
                <p className="text-sm text-yellow-400 mt-1">
                  Clean music only — explicit songs are hidden
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors ml-2 shrink-0"
              aria-label="Close search"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Search Form */}
          <div className="p-4 sm:p-6 border-b border-gray-700 shrink-0">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex space-x-3">
                <div className="flex-1">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search for songs..."
                    aria-label="Search for songs"
                    className="input-field w-full"
                    minLength={3}
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={query.trim().length < 3}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>

              {/* This session's music service */}
              <p className="text-xs text-gray-400">
                Searching {selectedMusicService === 'apple' ? 'Apple Music' : 'Spotify'} — this session plays through {selectedMusicService === 'apple' ? 'Apple Music' : 'Spotify'}
              </p>
            </form>
          </div>

          {/* Search Results */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
            {searchError?.response?.data?.error === 'spotify_not_connected' ? (
              <div className="text-center py-8">
                <Music className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-gray-300 font-medium mb-2">Spotify Not Connected</p>
                <p className="text-gray-400 text-sm">
                  The host needs to connect their Spotify account before search is available.
                </p>
              </div>
            ) : query.trim().length < 3 ? (
              visibleGoTos.length > 0 || visiblePicks.length > 0 ? (
                <div className="space-y-3">
                  {visibleGoTos.length > 0 && (
                    <>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-300 flex items-center">
                          <span className="mr-1.5">🔁</span>
                          Your go-tos
                        </h3>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Songs you bring to every party
                        </p>
                      </div>
                      {visibleGoTos.map(renderSongRow)}
                    </>
                  )}
                  {visiblePicks.length > 0 && (
                    <>
                      <div className={visibleGoTos.length > 0 ? 'pt-2' : ''}>
                        <h3 className="text-sm font-semibold text-gray-300 flex items-center">
                          <Music className="w-4 h-4 mr-1.5 text-purple-400" />
                          Based on what the party's playing
                        </h3>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Or search above for anything else
                        </p>
                      </div>
                      {visiblePicks.map(renderSongRow)}
                    </>
                  )}
                </div>
              ) : picksLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-4"></div>
                  <p className="text-gray-400">Finding songs the party will like...</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-400">Enter at least 3 characters to search</p>
                </div>
              )
            ) : isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-4"></div>
                <p className="text-gray-400">Searching...</p>
              </div>
            ) : searchResults?.length > 0 ? (
              <div className="space-y-3">
                {(!settings.explicitAllowed && getRole(session, user?.uid) !== 'host'
                  ? searchResults.filter((song) => !song.explicit)
                  : searchResults
                ).map(renderSongRow)}
              </div>
            ) : (
              <div className="text-center py-8">
                <Music className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-400">No songs found</p>
                <p className="text-gray-400 text-sm">Try a different search term</p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SongSearch; 